import React, { useState, useCallback, useMemo } from 'react'
import { Box, Flex, Avatar, Text } from '@chakra-ui/core'
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table'
import { useDispatch, useSelector } from 'react-redux'
import { Edit, Trash2 } from 'lucide-react'
import { IConfigRetailUser } from 'constants/interfaces'
import { TableContainer, Table } from '../commonUI/Table/styles'
import { getRoleBadgeProps, getRoleName } from './utils'
import { Pagination } from '../commonUI/Table/Pagination'
import { AppDispatch } from 'config/redux'
import { getUser } from 'redux/actions/admin'
import { ActionMenu } from 'components/AdminConsole/commonUI/ActionMenu'
import { useUserModals } from './UserModalsManagerContext'
import { isRtlSelector } from 'redux/selectors/general'
import LocaleText from 'components/LocaleText'
import { ConsoleBadge } from '../commonUI/ConsoleBadge'

const createColumns = (
  optionsList: { label: string; icon: React.ReactNode; onClick: (user: IConfigRetailUser) => void }[],
  userIdLoading: string | null
) => [
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_user_info" />
      </Text>
    ),
    accessor: 'first_name',
    sortType: 'alphanumeric',
    Cell: ({ row }: { row: { original: IConfigRetailUser } }) => {
      const user = row.original
      return (
        <Flex align="center" style={{ columnGap: '10px' }}>
          <Avatar
            name={`${user.first_name} ${user.last_name || ''}`}
            src={user.profile_img_url || user.avatar_profile_img_url}
            size="md"
          />
          <Box>
            <Text maxW="200px" isTruncated fontWeight="medium">{`${user.first_name} ${user.last_name || ''}`}</Text>
            <Text fontSize="sm" color="gray.600">
              {user.uid}
            </Text>
          </Box>
        </Flex>
      )
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_uid" />
      </Text>
    ),
    accessor: 'uid',
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_role" />
      </Text>
    ),
    accessor: 'user_group_role',
    Cell: ({ value }: { value: number }) => {
      const { text, background, border } = getRoleBadgeProps(value)
      return (
        <ConsoleBadge borderColor={border} bg={background} color={text}>
          {getRoleName(value)}
        </ConsoleBadge>
      )
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_desc" />
      </Text>
    ),
    accessor: 'desc',
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_sec_uid" />
      </Text>
    ),
    accessor: 'sec_uid',
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_actions" />
      </Text>
    ),
    accessor: 'admin_actions',
    Cell: ({ row }: { row: { original: IConfigRetailUser } }) => {
      const user = row.original
      const adaptedOptionsList = optionsList.map((option) => ({
        ...option,
        onClick: () => option.onClick(user),
      }))

      return (
        <Flex w="100%" justifyContent="flex-start">
          <ActionMenu
            isLoading={userIdLoading === user.uid}
            tooltipText="admin_edit_action"
            optionsList={adaptedOptionsList}
            triggerProps={{ pos: 'relative', top: 0, right: 0 }}
          />
        </Flex>
      )
    },
  },
]

export const UsersTable: React.FC<{ users: IConfigRetailUser[] }> = ({ users }) => {
  const dispatch: AppDispatch = useDispatch()
  const isRtl = useSelector(isRtlSelector)
  const { openUserModal, openGroupModal, requestDeleteUser } = useUserModals()

  const [userIdLoading, setUserIdLoading] = useState<string | null>(null)

  const handleGetUser = useCallback(
    async (user: IConfigRetailUser) => {
      try {
        setUserIdLoading(user.uid)
        const userData = await dispatch(getUser(user.uid))
        if (userData) {
          openUserModal(userData)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setUserIdLoading(null)
      }
    },
    [dispatch, openUserModal]
  )

  const optionsList = useMemo(
    () => [
      {
        label: 'admin_edit_action',
        icon: <Edit size={16} />,
        onClick: handleGetUser,
      },
      {
        label: 'edit_groups',
        icon: <Edit size={16} />,
        onClick: openGroupModal,
      },
      {
        label: 'admin_delete_action',
        icon: <Trash2 size={16} />,
        onClick: requestDeleteUser,
        color: 'red.500',
      },
    ],
    [handleGetUser, openGroupModal, requestDeleteUser]
  )

  const columns = useMemo(() => createColumns(optionsList, userIdLoading), [optionsList, userIdLoading])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: users,
      initialState: {
        pageIndex: 0,
        pageSize: 12,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  return (
    <>
      <TableContainer>
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any, index: number) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column: any, index: number) => (
                  <th {...column.getHeaderProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row: any, index: number) => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell: any, index: number) => (
                    <td {...cell.getCellProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableContainer>

      <Pagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </>
  )
}
